import { useState, useCallback, useRef, useEffect } from 'react'
import { Tooltip as MuiTooltip, type PopperProps } from '@mui/material'

import type { TooltipProps } from './types'

import * as S from './styles'

export const Tooltip = ({
  children,
  delay,
  width,
  contentWidth,
  maxWidth,
  singleLine = false,
  placement = 'bottom',
  ...rest
}: TooltipProps) => {
  const triggerWidth = contentWidth !== undefined ? contentWidth : width
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const computedEnterDelay = delay ?? 0
  const computedEnterNextDelay = computedEnterDelay as number

  const handleOpen = useCallback(() => {
    setOpen(true)
  }, [])

  const handleClose = useCallback(() => {
    setOpen(false)
  }, [])

  const handleMouseLeave = useCallback(() => {
    handleClose()
  }, [handleClose])

  useEffect(() => {
    if (!open) return

    let timeoutId: NodeJS.Timeout | null = null

    const handleDocumentMouseMove = (e: MouseEvent) => {
      if (!containerRef.current) return

      const containerRect = containerRef.current.getBoundingClientRect()
      const isOverContainer =
        e.clientX >= containerRect.left &&
        e.clientX <= containerRect.right &&
        e.clientY >= containerRect.top &&
        e.clientY <= containerRect.bottom

      const tooltipElement = document.querySelector('[role="tooltip"]') as HTMLElement
      let isOverTooltip = false
      if (tooltipElement) {
        const tooltipRect = tooltipElement.getBoundingClientRect()
        isOverTooltip =
          e.clientX >= tooltipRect.left &&
          e.clientX <= tooltipRect.right &&
          e.clientY >= tooltipRect.top &&
          e.clientY <= tooltipRect.bottom
      }

      if (!isOverContainer && !isOverTooltip) {
        if (timeoutId) {
          clearTimeout(timeoutId)
        }
        timeoutId = setTimeout(() => {
          handleClose()
        }, 50)
      } else {
        if (timeoutId) {
          clearTimeout(timeoutId)
          timeoutId = null
        }
      }
    }

    document.addEventListener('mousemove', handleDocumentMouseMove)
    return () => {
      document.removeEventListener('mousemove', handleDocumentMouseMove)
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
    }
  }, [open, handleClose])

  const popperSlot: Partial<PopperProps> | undefined = placement.startsWith('top')
    ? {
        modifiers: [
          { name: 'flip', enabled: false },
          {
            name: 'offset',
            options: {
              offset: [0, -10]
            }
          }
        ]
      }
    : undefined

  const isControlled = rest.open !== undefined

  return (
    <MuiTooltip
      arrow
      placement={placement}
      enterDelay={computedEnterDelay}
      enterNextDelay={computedEnterNextDelay}
      leaveDelay={0}
      disableInteractive
      open={isControlled ? rest.open : open}
      onOpen={rest.onOpen || handleOpen}
      onClose={rest.onClose || handleClose}
      slotProps={{
        popper: {
          ...(popperSlot ? { ...popperSlot } : {}),
          onMouseLeave: handleMouseLeave,
          style: { pointerEvents: 'none' }
        },
        tooltip: {
          sx: {
            ...S.tooltipStyles,
            ...(width !== undefined && { width }),
            ...(maxWidth !== undefined && { maxWidth }),
            ...(singleLine && { whiteSpace: 'nowrap' }),
            pointerEvents: 'none'
          },
          onMouseEnter: handleClose
        },
        arrow: {
          sx: S.arrowStyles
        }
      }}
      {...rest}
    >
      <S.Content ref={containerRef} style={{ width: triggerWidth }} onMouseLeave={handleMouseLeave}>
        {children}
      </S.Content>
    </MuiTooltip>
  )
}
